import { useThemeChanger } from './context';
import type { UseThemeChangerReturn } from './types';

/**
 * Enhanced hook that extends the base theme changer functionality
 * with convenience properties for easier usage
 *
 * @returns Theme changer state with convenience booleans
 *
 * @example
 * ```tsx
 * function ThemeStatus() {
 *   const { isLight, isDark, isSystem, themeMode, setThemeMode } = useThemeChangerExtended();
 *
 *   return (
 *     <div>
 *       <p>Current mode: {themeMode}</p>
 *       <p>Theme: {isLight ? 'Light' : 'Dark'}</p>
 *       {isSystem && <p>Following system preference</p>}
 *       <button onClick={() => setThemeMode('LIGHT_MODE')}>
 *         Light Mode
 *       </button>
 *       <button onClick={() => setThemeMode('DARK_MODE')}>
 *         Dark Mode
 *       </button>
 *       <button onClick={() => setThemeMode('SYSTEM')}>
 *         System
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useThemeChangerExtended(): UseThemeChangerReturn {
  const themeChanger = useThemeChanger();

  return {
    ...themeChanger,
    isLight: themeChanger.currentTheme === 'light',
    isDark: themeChanger.currentTheme === 'dark',
    isSystem: themeChanger.themeMode === 'SYSTEM',
  };
}

/**
 * Hook for simple theme toggle between light and dark modes
 * Ignores system mode and always sets explicit light/dark preference
 *
 * @returns Toggle function and current state
 *
 * @example
 * ```tsx
 * function SimpleThemeToggle() {
 *   const { isDark, toggleTheme, isLoading } = useThemeToggle();
 *
 *   return (
 *     <button onClick={toggleTheme} disabled={isLoading}>
 *       Switch to {isDark ? 'Light' : 'Dark'} Mode
 *     </button>
 *   );
 * }
 * ```
 */
export function useThemeToggle(): {
  isDark: boolean;
  isLight: boolean;
  toggleTheme: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
} {
  const { currentTheme, setThemeMode, isLoading, error } = useThemeChanger();

  const toggleTheme = async () => {
    const newMode = currentTheme === 'dark' ? 'LIGHT_MODE' : 'DARK_MODE';
    await setThemeMode(newMode);
  };

  return {
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light',
    toggleTheme,
    isLoading,
    error,
  };
}

/**
 * Hook for accessing theme mode only (without resolved theme)
 * Useful for components that need to know the user's preference setting
 *
 * @returns Theme mode state and setter
 *
 * @example
 * ```tsx
 * function ThemeModeIndicator() {
 *   const { themeMode, setThemeMode, isLoading } = useThemeMode();
 *
 *   return (
 *     <div>
 *       <span>Mode: {themeMode}</span>
 *       <select
 *         value={themeMode}
 *         onChange={(e) => setThemeMode(e.target.value as any)}
 *         disabled={isLoading}
 *       >
 *         <option value="LIGHT_MODE">Light</option>
 *         <option value="DARK_MODE">Dark</option>
 *         <option value="SYSTEM">System</option>
 *       </select>
 *     </div>
 *   );
 * }
 * ```
 */
export function useThemeMode(): {
  themeMode: 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM';
  setThemeMode: (mode: 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM') => Promise<void>;
  isLoading: boolean;
  error: Error | null;
} {
  const { themeMode, setThemeMode, isLoading, error } = useThemeChanger();

  return {
    themeMode,
    setThemeMode,
    isLoading,
    error,
  };
}

/**
 * Hook for checking current theme without providing setter
 * Useful for components that only need to read the theme state
 *
 * @returns Current theme information
 *
 * @example
 * ```tsx
 * function ThemedComponent() {
 *   const { currentTheme, isLight, isDark } = useThemeState();
 *
 *   return (
 *     <div className={isDark ? 'dark-styles' : 'light-styles'}>
 *       Current theme: {currentTheme}
 *     </div>
 *   );
 * }
 * ```
 */
export function useThemeState(): {
  currentTheme: 'light' | 'dark';
  isLight: boolean;
  isDark: boolean;
  themeMode: 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM';
} {
  const { currentTheme, themeMode } = useThemeChanger();

  return {
    currentTheme,
    isLight: currentTheme === 'light',
    isDark: currentTheme === 'dark',
    themeMode,
  };
}
